/* eslint-disable no-prototype-builtins */
import { getObjectByKeys } from "./commonHelpers";

const getServerErrorMessage = (message) => {
  if (Array.isArray(message)) {
    return message[0];
  }
  return message;
};

const setPackingListServerErrors = (packingListErrors, formData, setError) => {
  packingListErrors.forEach((packingListError) => {
    const goodCustomsInformationError = packingListError.good_customs_information;
    if (packingListError.title) {
      const errorTitleArr = packingListError.title.split("_");
      const errorIndex = errorTitleArr.pop();
      packingListError.title = errorTitleArr.join("_");
      if (
        formData.hasOwnProperty("packing_list") &&
        formData?.packing_list?.[errorIndex]?.hasOwnProperty?.(packingListError.title)
      ) {
        setError(`packing_list.${errorIndex}.${packingListError.title}`, {
          type: "serverError",
          message: getServerErrorMessage(packingListError?.message || "Something went wrong"),
        });
      }
    } else if (goodCustomsInformationError && Array.isArray(goodCustomsInformationError)) {
      goodCustomsInformationError.forEach((customsError) => {
        if (customsError.title === "hs_code") {
          formData.packing_list?.forEach((item, itemIndex) => {
            const goodCustomsInformation = item?.good_customs_information;
            if (goodCustomsInformation?.length > 0 && !goodCustomsInformation?.[0].hs_code) {
              setError(`packing_list.${itemIndex}.good_customs_information.0.hs_code`, {
                type: "serverError",
                message: getServerErrorMessage(customsError?.message || "Something went wrong"),
              });
            }
          });
        }
      });
    }
  });
};

const CUSTOM_ERROR_KEYS_MAPPING = {
  pickup_address_reference_number: "pickup_address_name",
  destination_address_reference_number: "stop1_address_name",
};

export const resolveServerErrors = (error, formData, setError) => {
  let data;
  if (error?.response?.data && typeof error?.response?.data === "object") {
    /**
     * Deep clone error object so we don't mutate anything in orignal error object
     * which is then passed to show toast errors.
     */
    data = JSON.parse(JSON.stringify(error.response.data));
  }
  if (data && Object.keys(data)?.length) {
    if (data.packing_list) {
      const packingListErrors = data.packing_list;
      delete data.packing_list;
      setPackingListServerErrors(packingListErrors, formData, setError);
    }

    if (data.shipment?.confirmation_time) delete data.shipment.confirmation_time;
    if (data.shipment?.quoting_deadline_time) delete data.shipment.quoting_deadline_time;

    Object.keys(data).forEach((errorKey) => {
      if (Array.isArray(data[errorKey])) {
        data[errorKey].forEach((errData) => {
          if (errData.title && formData.hasOwnProperty(errorKey) && formData?.[errorKey]?.hasOwnProperty(errData.title)) {
            const errTitle = CUSTOM_ERROR_KEYS_MAPPING[errData.title] || errData.title;
            setError(`${errorKey}.${errTitle}`, {
              type: "serverError",
              message: getServerErrorMessage(errData?.message || "Something went wrong"),
            });
          }
        });
      } else if (typeof data[errorKey] === "object" && Object.keys(data[errorKey])) {
        /**
         * for draft errors we don't get an array instead an object.
         */
        Object.keys(data[errorKey]).forEach((key) => {
          if (
            formData.hasOwnProperty(errorKey) &&
            formData?.[errorKey]?.hasOwnProperty(key) &&
            Array.isArray(data[errorKey][key]) &&
            data[errorKey][key]?.length === 1
          ) {
            setError(`${errorKey}.${key}`, {
              type: "serverError",
              message: getServerErrorMessage(data[errorKey][key][0] || "Something went wrong"),
            });
          }
        });
      }
    });
  }
};

export const hasServerError = (errors) => {
  let hasError = false;

  // eslint-disable-next-line guard-for-in, no-restricted-syntax
  for (const key in errors) {
    if (Array.isArray(errors[key])) {
      // eslint-disable-next-line no-restricted-syntax
      for (const item of errors[key]) {
        // eslint-disable-next-line guard-for-in, no-restricted-syntax
        for (const subKey in item) {
          if (item[subKey]?.type === "serverError") {
            hasError = true;
            break;
          }
        }
      }
    } else {
      // eslint-disable-next-line guard-for-in, no-restricted-syntax
      for (const subKey in errors[key]) {
        if (errors[key][subKey]?.type === "serverError") {
          hasError = true;
          break;
        }
      }
    }
    if (hasError) {
      break;
    }
  }
  return hasError;
};

export const orderOrganizationalFields = (organizationalOptions, order) => {
  const itemsToFilter = [];
  const itemWithoutFilter = [];
  const filteredItems = [];
  let itemsToFilterHashMap = {};
  organizationalOptions.forEach((option) => {
    if (order.includes(option.field_id)) {
      itemsToFilter.push(option);
    } else {
      itemWithoutFilter.push(option);
    }
  });
  itemsToFilterHashMap = getObjectByKeys(itemsToFilter, "field_id");
  order.forEach((item) => filteredItems.push(itemsToFilterHashMap[item]));

  return [...itemWithoutFilter, ...filteredItems].filter((item) => item);
};

export const parseDraftValues = (data) => {
  // deep cloning so orignal RHF state don't get mutated.
  const clonedData = JSON.parse(JSON.stringify(data));

  if (typeof clonedData.quick_request_id === "string") {
    clonedData.available_quick_requests = [clonedData.quick_request_id];
    delete clonedData.quick_request_id;
  }

  // If the address type is not either port or airport and it is not a quick request, we remove the state.
  if (!clonedData.quick_request && clonedData.schedule) {
    if (clonedData.schedule.pickup_address_type !== "address") delete clonedData.schedule.pickup_state;
    if (clonedData.schedule.stop1_address_type !== "address") delete clonedData.schedule.stop1_state;
  }

  // remove road_freight_option if road_freight is false
  if (!clonedData?.invited_modes?.road_freight && clonedData?.invited_modes?.hasOwnProperty("road_freight_option")) {
    delete clonedData.invited_modes.road_freight_option;
  }

  // transform incoterms value to uppercase
  if (clonedData?.service?.incoterms) {
    clonedData.service.incoterms = clonedData.service.incoterms.toUpperCase();
  }

  // Transform 'NONE' value of incoterms to 'None' or backend won't accept
  if (clonedData?.service?.incoterms === "NONE") {
    clonedData.service.incoterms = "None";
  }

  // if packing list custom values are totally empty remove them
  if (clonedData?.packing_list && Array.isArray(clonedData.packing_list)) {
    clonedData.packing_list = clonedData.packing_list.map((packingList) => {
      const updatedData = { ...packingList };
      if (packingList?.good_customs_information?.length) {
        updatedData.good_customs_information = packingList.good_customs_information.filter(
          (item) => item.value || item.description || item.hs_code || item.attachment,
        );
      }
      return updatedData;
    });
  }

  if (clonedData?.service?.special_requirements) {
    Object.keys(clonedData.service.special_requirements).forEach((key) => {
      const value = clonedData.service.special_requirements?.[key]?.value;
      const isEnabled = value === true || value === "true";
      clonedData.service.special_requirements[key].value = isEnabled;
      if (!isEnabled) {
        clonedData.service.special_requirements[key].additional_information = "";
      }
    });
  }

  // Keep the full `schedule` object on draft save even when `stops` is present (multistop /
  // multi-drop). Replacing schedule with only { stops, multistop_instructions } dropped
  // notify_party, consignee, contact fields, and other keys that share the same shape as
  // the classic (non-multistop) schedule payload.

  // modify data if needed before sending as a draft
  return clonedData;
};

export const modifyDraftData = (data, isDuplicated, isQuoteRequest, isNewAddressStructureEnabled = false) => {
  // lower case incoterms and service_type in order to pre-fill correct values in the form
  if (data?.service?.incoterms) {
    data.service.incoterms = data.service.incoterms.toLowerCase();
  } else {
    if (!data.service) {
      data.service = {};
    }
    data.service.incoterms = "none";
  }
  if (data?.service?.service_type) data.service.service_type = data.service.service_type.toLowerCase();

  // prepend "Copy" to the shipment title if it is a duplicate
  if (isDuplicated) {
    if (isQuoteRequest) {
      data.shipment.title = `Copy ${data.shipment.title}`;
    } else {
      data.quick_request.title = `Copy ${data.quick_request.title}`;
    }
    if (data.initial_invited_modes) {
      delete data.initial_invited_modes;
    }
  }
  // transform cost_recovery_required value to string as backend expects that
  if (data?.organizational?.hasOwnProperty("cost_recovery_required")) {
    data.organizational.cost_recovery_required = data.organizational.cost_recovery_required ? "True" : "False";
  }
  // if its a converted quick reqeust set the shipment title
  if (isQuoteRequest && data?.quick_request?.title) {
    if (!data.shipment) {
      data.shipment = {};
    }
    data.shipment.title = data.quick_request.title;
  }
  /**
   * check if address setting is enabled and reference number is not there
   * that means address is soft deleted and we need to remove the other
   * address fields to prevent autofilling.
   */
  if (isNewAddressStructureEnabled && data?.schedule) {
    if (!data?.schedule?.pickup_address_reference_number) {
      getAddressFields("pickup").forEach((fieldName) => {
        data.schedule[fieldName] = "";
      });
    }
    if (!data?.schedule?.destination_address_reference_number) {
      getAddressFields("stop1").forEach((fieldName) => {
        data.schedule[fieldName] = "";
      });
    }
  }
  // Integration / system drafts may omit packing_list entirely; don't throw on load.
  if (Array.isArray(data.packing_list)) {
    data.packing_list = data.packing_list.map((commodity) => ({
      ...commodity,
      weight_piece: commodity.w_unit === "lbs" ? commodity.weight_piece_lbs || commodity.weight_piece : commodity.weight_piece,
    }));
  }

  if (data?.schedule?.stops?.length > 0) {
    data.schedule.stops = data.schedule.stops.map((stop) => ({
      ...stop,
      stop_before_date: stop.stop_before_date || stop.stop_after_date,
      stop_before_time: stop.stop_before_time || stop.stop_after_time,
    }));
  }

  return data;
};

export const getAllFiles = (allFiles) =>
  allFiles.filter((tag, index, array) => array.findIndex((t) => t.reference === tag.reference) === index);

const ADDRESS_FIELDS = [
  "address_name",
  "zipcode",
  "street",
  "street_line2",
  "city",
  "country",
  "country_code",
  "timezone",
  "state",
  "custom_address_reference",
  "location_reference_number",
  "un_locode",
];

const getAddressFields = (locationType) => ADDRESS_FIELDS.map((field) => `${locationType}_${field}`);
